/* eslint-disable no-restricted-syntax */
import {
  apiPlugin,
  getStoryblokApi,
  StoryblokComponent,
  storyblokInit,
} from '@storyblok/react';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import { useEffect } from 'react';

import Meta from '@/components/core/Meta';
import StoryblokComponents from '@/components/core/StoryblokComponents';
import Layout from '@/components/templates/Layout';
import {
  footerRelations,
  menuRelations,
  projectFooterRelations,
  resolveRelations,
} from '@/helpers/resolveRelations';
import { useEditableContent } from '@/helpers/useEditableContent';
import { useStore } from '@/lib/store';

const Page: InferGetStaticPropsType<typeof getStaticProps> = ({
  story: s,
  settingsHeader,
  settingsFooter,
  settingsFooterProject,
  settingsNotifications,
}) => {
  const story = useEditableContent(s);

  const metaTitle = story.content?.seo_title
    ? story.content?.seo_title
    : story.name;
  let metaImageUrl;
  const metaDescription = story.content?.seo_description;

  useEffect(() => {
    useStore.getState().setFooterData(settingsFooter.story.content);
  }, [settingsFooter]);

  return (
    <>
      <Meta
        blok={story.content}
        title={metaTitle}
        description={metaDescription}
        image={
          metaImageUrl || `${story.content?.seo_image?.filename}/m/1200x630`
        }
      />
      <Layout
        story={story}
        blok={story.content}
        settingsNotifications={settingsNotifications}
        footer={true}
      >
        <StoryblokComponent
          story={story}
          blok={story.content}
          settingsHeader={settingsHeader.story.content}
          settingsFooter={settingsFooter.story.content}
          settingsFooterProject={settingsFooterProject.story.content}
          settingsNotifications={settingsNotifications}
          settomgs
        />
      </Layout>
    </>
  );
};

function Now() {
  const date = new Date();

  const year = date.toLocaleString('default', { year: 'numeric' });
  const month = date.toLocaleString('default', { month: '2-digit' });
  const day = date.toLocaleString('default', { day: '2-digit' });

  return `${year}-${month}-${day} ${date.getHours()}:${date.getMinutes()}`;
}

storyblokInit({
  accessToken: process.env.STORYBLOK_API,
  use: [apiPlugin],
  apiOptions: {
    region: 'us',
  },
  components: StoryblokComponents,
});

export const getStaticProps: GetStaticProps = async ({ params, preview }) => {
  const slug = params.slug
    ? `pages/${(params.slug as string[]).join('/')}`
    : 'pages/home';

  const storyblokApi = await getStoryblokApi();

  const stroyblokVersion =
    process.env.NEXT_PUBLIC_STORYBLOK_VERSION === 'published'
      ? 'published'
      : 'draft';

  const { data: settingsHeaderData } = await storyblokApi.get(
    `cdn/stories/settings/header-settings/`,
    {
      version: preview ? 'draft' : stroyblokVersion,
      resolve_relations: menuRelations.join(','),
      resolve_links: 'story',
    }
  );

  const { data: settingsNotificationsData } = await storyblokApi.get(
    `cdn/stories/`,
    {
      version:
        process.env.NEXT_PUBLIC_STORYBLOK_VERSION === 'published'
          ? 'published'
          : 'draft',
      starts_with: 'notifications/',
      per_page: 1,
      content_type: 'Notification',
      filter_query: {
        StartDate: {
          lte_date: Now(),
        },
        EndDate: {
          gte_date: Now(),
        },
      },
    }
  );

  const { data: settingsFooterData } = await storyblokApi.get(
    `cdn/stories/settings/footer-settings/`,
    {
      version:
        process.env.NEXT_PUBLIC_STORYBLOK_VERSION === 'published'
          ? 'published'
          : 'draft',
      resolve_relations: footerRelations.join(','),
      resolve_links: 'story',
    }
  );

  const { data: settingsProjectFooterData } = await storyblokApi.get(
    `cdn/stories/settings/projects-footer-settings/`,
    {
      version:
        process.env.NEXT_PUBLIC_STORYBLOK_VERSION === 'published'
          ? 'published'
          : 'draft',
      resolve_relations: projectFooterRelations.join(','),
      resolve_links: 'story',
    }
  );

  const { data } = await storyblokApi.get(`cdn/stories/${slug}`, {
    version: preview ? 'draft' : stroyblokVersion,
    resolve_relations: resolveRelations.join(','),
    resolve_links: 'story',
  });

  return {
    props: {
      story: data ? data.story : false,
      key: data ? data.story.id : false,
      settingsHeader: settingsHeaderData || false,
      settingsFooter: settingsFooterData || false,
      settingsFooterProject: settingsProjectFooterData || false,
      settingsNotifications: settingsNotificationsData || false,
    },
    revalidate: 604800,
  };
};

export async function getStaticPaths() {
  const storyblokApi = getStoryblokApi();
  const { data } = await storyblokApi.get('cdn/links/', {
    starts_with: 'pages',
    version:
      process.env.NEXT_PUBLIC_STORYBLOK_VERSION === 'published'
        ? 'published'
        : 'draft',
  });

  const paths = [];
  Object.keys(data.links).forEach((linkKey) => {
    if (
      data.links[linkKey].is_folder ||
      data.links[linkKey].slug === 'pages/home'
    )
      return;

    const { slug } = data.links[linkKey];
    const splittedSlug = slug.split('/').slice(1);

    paths.push({ params: { slug: splittedSlug } });
  });

  paths.push({ params: { slug: false } });

  return {
    paths,
    fallback: false,
  };
}

export default Page;
